import { Button } from '@heidi/design-system/button';
import { Square } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import type { DictationState } from '../store/useVoiceAssistantStore';
import { voiceAssistantMessages as messages } from './voice-assistant.messages';

interface VoiceDictationBarProps {
  dictation: DictationState;
  onStop: () => void;
}

/**
 * Replaces the composer while mic audio is routed to the transcription model.
 * The clinician needs a visible answer to "is this being captured, and does it
 * have my words right" — silence here reads as a dropped dictation.
 */
export function VoiceDictationBar({ dictation, onStop }: VoiceDictationBarProps) {
  const intl = useIntl();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { committed, interim } = dictation;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [committed, interim]);

  const hasText = Boolean(committed || interim);

  return (
    <div
      className="flex items-start gap-2 rounded-xl bg-background-secondary p-2"
      data-testid="voice-assistant-dictation-bar"
    >
      <span
        className="mt-1.5 size-2 shrink-0 animate-pulse rounded-full bg-validation-destructive"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
          {intl.formatMessage(messages.dictationHeading)}
        </p>
        <div
          ref={scrollRef}
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          className="scrollbar-fade max-h-24 overflow-y-auto overscroll-contain text-sm text-text-primary"
          data-testid="voice-assistant-dictation-text"
        >
          {hasText ? (
            <p className="text-pretty">
              {committed}
              {/* A real space, not a margin: this text is read out as one
                  sentence and copied as one string. */}
              {committed && interim ? ' ' : null}
              {interim ? <span className="text-text-tertiary">{interim}</span> : null}
            </p>
          ) : (
            <p className="text-text-tertiary">
              {intl.formatMessage(messages.dictationHint)}
            </p>
          )}
        </div>
      </div>
      <Button
        variant="destructive_soft"
        size="sm"
        iconOnly
        aria-label={intl.formatMessage(messages.stopDictation)}
        onClick={onStop}
        className="size-9 shrink-0 rounded-full"
        data-testid="voice-assistant-stop-dictation"
      >
        <Square className="size-3.5 fill-current" />
      </Button>
    </div>
  );
}
